import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useSession } from './auth';
import { getActiveProject, getMe, getProfile } from './api';
import { isProfileComplete, hasRequiredUserFields } from './lib/profileCompleteness';
import { Welcome } from './screens/Welcome';
import { ProjectSetup } from './screens/ProjectSetup';
import { ProjectComplete } from './screens/ProjectComplete';
import { Dashboard } from './screens/Dashboard';
import { CheckIn } from './screens/CheckIn';
import { Coach } from './screens/Coach';
import { CoachNotes } from './screens/CoachNotes';
import { Profile } from './screens/Profile';
import { WorkoutChat } from './screens/WorkoutChat';
import { SignIn } from './screens/SignIn';
import { AgeGate } from './screens/AgeGate';
import { Settings } from './screens/Settings';
import { Terms } from './screens/Terms';
import { TabBar } from './components/TabBar';
import { Spinner } from './components/Spinner';

// Decides where to send a freshly-loaded user. In order:
//   1. no session                                 -> /sign-in
//   2. session, missing date_of_birth             -> /age-gate
//   3. session, DOB, profile missing any required -> /welcome
//      field (see lib/profileCompleteness)
//   4. session, DOB, complete profile, no project -> /projects/new (S7)
//   5. session, DOB, complete profile, project    -> /dashboard
//
// "Complete" is defined once in lib/profileCompleteness — the exact field set
// POST /projects/preview enforces (sex, height_cm, weight_kg, activity_level).
// Previously BootGate only checked sex, so a user who finished Welcome without
// height/weight sailed through to /projects/new and dead-ended at the timeline
// step with a 409 "Profile incomplete". Routing on the full set closes that gap
// at the same place Welcome's submit gate enforces it.
//
// The no-project case (S7) routes through /projects/new on initial load
// only — BootGate fires on path '/', not on internal navigation. After
// in-app cancel, the Dashboard's no-active-state CTA handles the prompt
// to start a new project; we don't auto-redirect mid-session.
function BootGate() {
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate('/sign-in', { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all([getMe(), getProfile(), getActiveProject()])
      .then(([user, profile, projectRes]) => {
        if (cancelled) return;
        if (!hasRequiredUserFields(user)) {
          navigate('/age-gate', { replace: true });
          return;
        }
        if (!isProfileComplete(profile)) {
          navigate('/welcome', { replace: true });
          return;
        }
        navigate(projectRes.project ? '/dashboard' : '/projects/new', { replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        // If /users/me, /profile, or /projects/active failed, default to
        // /age-gate so a user with no DOB still hits the gate before
        // anything else. Welcome and the project-aware split fall out
        // naturally on subsequent successful loads.
        navigate('/age-gate', { replace: true });
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, loading, navigate]);

  if (loading || checking) return <div className="center"><Spinner /></div>;
  return null;
}

function RequireSession({ children }: { children: React.ReactElement }) {
  const { session, loading } = useSession();
  if (loading) return <div className="center"><Spinner /></div>;
  return session ? children : <Navigate to="/sign-in" replace />;
}

export function App() {
  const { session } = useSession();
  return (
    <BrowserRouter>
      <div className="app">
        <Routes>
          <Route path="/" element={<BootGate />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/sign-in" element={session ? <Navigate to="/" replace /> : <SignIn />} />
          <Route path="/age-gate" element={<RequireSession><AgeGate /></RequireSession>} />
          <Route path="/welcome" element={<RequireSession><Welcome /></RequireSession>} />
          <Route path="/projects/new" element={<RequireSession><ProjectSetup /></RequireSession>} />
          <Route path="/projects/:id/complete" element={<RequireSession><ProjectComplete /></RequireSession>} />
          <Route path="/projects/:id/workout-chat" element={<RequireSession><WorkoutChat /></RequireSession>} />
          <Route path="/dashboard" element={<RequireSession><Dashboard /></RequireSession>} />
          <Route path="/checkin" element={<RequireSession><CheckIn /></RequireSession>} />
          <Route path="/coach" element={<RequireSession><Coach /></RequireSession>} />
          <Route path="/coach-notes" element={<RequireSession><CoachNotes /></RequireSession>} />
          <Route path="/profile" element={<RequireSession><Profile /></RequireSession>} />
          <Route path="/settings" element={<RequireSession><Settings /></RequireSession>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {session ? <TabBar /> : null}
      </div>
    </BrowserRouter>
  );
}
